import { TypeManagerDecorator } from "../../../main/type.map.service";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    ViewEncapsulation,
} from "@angular/core";
import { S25Util } from "../../../util/s25-util";
import { IngestionFieldDefinition, IngestionPoint, DataIngestionService, AnswerType } from "../data.ingestion.service";
import { Rules } from "../../s25-rule-tree/s25.rule.const";
import { WorkflowService } from "../../workflow/service/workflow.service";
import { Workflow } from "../../workflow/service/Workflow";

@TypeManagerDecorator("s25-ng-admin-ingestion-form")
@Component({
    selector: "s25-ng-admin-ingestion-form",
    template: `
        @if (init) {
            <!-- Form Details Section -->
            <div class="form-details-section c-margin-bottom--double">
                <h3 class="c-margin-bottom--single">Form Details</h3>

                <div class="c-margin-bottom--single">
                    <label class="ngBlock">
                        Form Name:
                        <input
                            class="c-input"
                            type="text"
                            [(ngModel)]="ingestionPoint.ingestionPointName"
                            placeholder="Enter form name"
                        />
                    </label>
                </div>

                <div class="c-margin-bottom--single">
                    <label class="ngBlock">
                        Description (optional):
                        <textarea
                            class="cn-form__control"
                            [(ngModel)]="ingestionPoint.description"
                            placeholder="Enter a description for this form"
                            rows="3"
                        ></textarea>
                    </label>
                </div>

                <div class="c-margin-bottom--single">
                    <label class="ngBlock">
                        Category:
                        <div class="category-display">{{ ingestionPoint.category }}</div>
                    </label>
                </div>

                <div class="c-margin-bottom--single">
                    <label class="ngBlock">
                        Sub-Category:
                        <s25-ng-ingestion-subcategory-selector
                            [category]="ingestionPoint.category!"
                            [subCategory]="ingestionPoint.subCat!"
                            [isEditing]="false"
                            [onSubCategoryChange]="onSubCategoryChange"
                        ></s25-ng-ingestion-subcategory-selector>
                    </label>
                </div>

                <div class="c-margin-bottom--single">
                    <s25-ng-checkbox [parentCd]="cd" [(modelValue)]="ingestionPoint.enabled!">
                        Form Enabled
                    </s25-ng-checkbox>
                </div>

                <div class="c-margin-bottom--single">
                    <h4 class="c-margin-bottom--half">Active Date Range</h4>
                    <p class="form-help-text c-margin-bottom--single">
                        Set when this form is available for submissions. Leave blank to allow submissions at any time.
                    </p>

                    <div class="date-range-section">
                        <div class="c-margin-bottom--single">
                            <label class="ngBlock">
                                Active Start Date (optional):
                                <input
                                    class="c-input"
                                    type="datetime-local"
                                    [(ngModel)]="ingestionPoint.activeStartDate"
                                    [placeholder]="'Leave blank for no start date'"
                                />
                            </label>
                        </div>

                        <div class="c-margin-bottom--single">
                            <label class="ngBlock">
                                Active End Date (optional):
                                <input
                                    class="c-input"
                                    type="datetime-local"
                                    [(ngModel)]="ingestionPoint.activeEndDate"
                                    [placeholder]="'Leave blank for no end date'"
                                />
                            </label>
                        </div>
                    </div>
                </div>

                <div class="c-margin-bottom--single">
                    <label class="ngBlock">
                        Workflow Assignment:
                        <div class="workflow-assignment-section">
                            <div class="workflow-option">
                                <label class="workflow-option-label">
                                    <input
                                        type="radio"
                                        name="workflowType"
                                        value="none"
                                        [(ngModel)]="workflowAssignmentType"
                                        (ngModelChange)="onWorkflowTypeChange()"
                                    />
                                    <span class="workflow-option-text">No Workflow Process</span>
                                </label>
                            </div>

                            <div class="workflow-option">
                                <label class="workflow-option-label">
                                    <input
                                        type="radio"
                                        name="workflowType"
                                        value="direct"
                                        [(ngModel)]="workflowAssignmentType"
                                        (ngModelChange)="onWorkflowTypeChange()"
                                    />
                                    <span class="workflow-option-text">Direct Workflow Assignment</span>
                                </label>
                                @if (workflowAssignmentType === "direct") {
                                    <div class="workflow-selector">
                                        <select class="cn-form__control" [(ngModel)]="ingestionPoint.workflowId">
                                            <option value="">-- Select Workflow Process --</option>
                                            @for (workflow of availableWorkflows; track workflow.workflowProcessId) {
                                                <option [value]="workflow.workflowProcessId">
                                                    {{ workflow.workflowProcessName }}
                                                </option>
                                            }
                                        </select>
                                    </div>
                                }
                            </div>

                            <div class="workflow-option">
                                <label class="workflow-option-label">
                                    <input
                                        type="radio"
                                        name="workflowType"
                                        value="default"
                                        [(ngModel)]="workflowAssignmentType"
                                        (ngModelChange)="onWorkflowTypeChange()"
                                    />
                                    <span class="workflow-option-text"
                                        >Default Workflow for Rules-Based Assignment</span
                                    >
                                </label>
                                @if (workflowAssignmentType === "default") {
                                    <div class="workflow-selector">
                                        <select class="cn-form__control" [(ngModel)]="ingestionPoint.defaultWorkflowId">
                                            <option value="">-- Select Default Workflow Process --</option>
                                            @for (workflow of availableWorkflows; track workflow.workflowProcessId) {
                                                <option [value]="workflow.workflowProcessId">
                                                    {{ workflow.workflowProcessName }}
                                                </option>
                                            }
                                        </select>
                                    </div>
                                }
                            </div>
                        </div>
                    </label>
                    <div class="form-help-text">
                        <strong>Direct Assignment:</strong> All form submissions will be assigned to the selected
                        workflow process.<br />
                        <strong>Default for Rules:</strong> Use rules to determine workflow assignment, with the
                        selected workflow as fallback when no rules match.
                    </div>
                </div>
            </div>

            <!-- Form Fields Section -->
            <div class="form-fields-section">
                <div class="section-header">
                    <h3>Form Fields</h3>
                    <button class="aw-button aw-button--outline" (click)="addFieldDefinition()">+ Add Field</button>
                </div>

                @for (fieldDef of fieldDefinitions; track fieldDef; let idx = $index) {
                    <div class="field-card">
                        <!-- Field Header -->
                        <div class="field-header">
                            <div class="field-title">
                                <h4>{{ fieldDef.fieldLabel || "Untitled Field" }}</h4>
                                <div class="field-badges">
                                    @if (isSystemField(fieldDef)) {
                                        <span class="badge badge-warning">System Field</span>
                                    }
                                    @if (fieldDef.hidden) {
                                        <span class="badge badge-secondary">Hidden</span>
                                    }
                                    @if (fieldDef.required) {
                                        <span class="badge badge-primary">Required</span>
                                    }
                                </div>
                            </div>
                            <div class="action-controls">
                                <div class="move-buttons">
                                    <button
                                        class="aw-button aw-button--outline move-btn"
                                        (click)="move(fieldDef, -1)"
                                        [disabled]="idx === 0"
                                        title="Move up"
                                    >
                                        ↑
                                    </button>
                                    <button
                                        class="aw-button aw-button--outline move-btn"
                                        (click)="move(fieldDef, 1)"
                                        [disabled]="idx === fieldDefinitions.length - 1"
                                        title="Move down"
                                    >
                                        ↓
                                    </button>
                                </div>
                                <button
                                    class="aw-button aw-button--danger--outline"
                                    (click)="removeFieldDefinition(fieldDef)"
                                    [disabled]="isSystemField(fieldDef)"
                                    title="Remove field"
                                >
                                    Remove
                                </button>
                            </div>
                        </div>

                        <!-- Field Configuration -->
                        <div class="field-config">
                            <!-- Basic Settings -->
                            <div class="config-group">
                                <h5>Basic Settings</h5>
                                <div class="form-row">
                                    <div class="form-group">
                                        <label>Field Label</label>
                                        <input
                                            class="form-control"
                                            type="text"
                                            [(ngModel)]="fieldDef.fieldLabel"
                                            (ngModelChange)="onFieldLabelChange(fieldDef)"
                                            name="fieldLabel-{{ idx }}"
                                            placeholder="Enter field label"
                                            [disabled]="isSystemField(fieldDef)"
                                        />
                                    </div>
                                    <div class="form-group">
                                        <label>Field Type</label>
                                        <select
                                            class="form-control"
                                            [(ngModel)]="fieldDef.fieldType"
                                            (ngModelChange)="onFieldTypeChange(fieldDef)"
                                            [disabled]="isSystemField(fieldDef)"
                                        >
                                            <option value="SELECT">Select</option>
                                            <option value="MULTISELECT">Multi-Select</option>
                                            <option value="TEXT">User Input</option>
                                            <option value="FILE">File Upload</option>
                                            <option value="SECTION">Section Header</option>
                                            <option value="EVENT_TYPE">Event Type</option>
                                            <option value="ORGANIZATION">Organization</option>
                                            <option value="OBJECT_OCCURRENCE">Object Occurrences</option>
                                            <option value="FORM">Form</option>
                                            <option value="REGISTRANTS">Registrants</option>
                                        </select>
                                    </div>
                                    @if (fieldDef.fieldType !== "SECTION") {
                                        <div class="form-group">
                                            <label>Answer Type</label>
                                            <select
                                                class="form-control"
                                                [(ngModel)]="fieldDef.answerType"
                                                (ngModelChange)="onAnswerTypeChange(fieldDef)"
                                                [disabled]="
                                                    isSystemField(fieldDef) ||
                                                    isJsonField(fieldDef) ||
                                                    fieldDef.fieldType === 'FILE' ||
                                                    fieldDef.fieldType === 'EVENT_TYPE' ||
                                                    fieldDef.fieldType === 'ORGANIZATION' ||
                                                    fieldDef.fieldType === 'FORM' ||
                                                    fieldDef.fieldType === 'REGISTRANTS'
                                                "
                                            >
                                                @if (fieldDef.fieldType === "FILE") {
                                                    <option value="FILE">File</option>
                                                } @else if (isJsonField(fieldDef)) {
                                                    <option value="JSON">JSON</option>
                                                } @else {
                                                    <option value="STRING">String (< 256 chars)</option>
                                                    <option value="LARGE_STRING">Large String (>= 256 chars)</option>
                                                    <option value="INTEGER">Integer</option>
                                                    <option value="FLOAT">Float</option>
                                                    <option value="BOOLEAN">Boolean</option>
                                                    <option value="DATETIME">Date/Time</option>
                                                }
                                            </select>
                                        </div>
                                    }
                                </div>

                                <div class="form-row">
                                    <div class="form-group">
                                        <label>Reporting Field Code</label>
                                        <input
                                            class="form-control"
                                            [class.is-invalid]="!isValidReportingCode(fieldDef.reportingFieldCode)"
                                            type="text"
                                            [(ngModel)]="fieldDef.reportingFieldCode"
                                            (ngModelChange)="onReportingCodeChange(fieldDef)"
                                            placeholder="Auto-generated from field label"
                                            maxlength="32"
                                            [disabled]="isSystemField(fieldDef)"
                                        />
                                        @if (
                                            fieldDef.reportingFieldCode &&
                                            !isValidReportingCode(fieldDef.reportingFieldCode)
                                        ) {
                                            <div class="invalid-feedback">
                                                Only alphanumeric characters allowed, max 32 characters
                                            </div>
                                        }
                                        <small class="form-text">Used for cross-form reporting</small>
                                    </div>
                                </div>
                            </div>

                            <!-- Field Options -->
                            <div class="config-group">
                                <h5>Field Options</h5>
                                <div class="form-row">
                                    <div class="form-group">
                                        <label>Info Text</label>
                                        <textarea
                                            class="form-control"
                                            [(ngModel)]="fieldDef.infoText"
                                            rows="2"
                                            placeholder="Optional help text for users"
                                            [disabled]="isSystemField(fieldDef)"
                                        ></textarea>
                                    </div>
                                </div>

                                <div class="form-row">
                                    <div class="form-group">
                                        <label>Default Value</label>
                                        <input
                                            class="form-control"
                                            [class.is-invalid]="isValidDefaultValue(fieldDef) !== null"
                                            type="text"
                                            [(ngModel)]="fieldDef.defaultValue"
                                            [placeholder]="getDefaultValuePlaceholder(fieldDef)"
                                            [disabled]="isSystemField(fieldDef)"
                                        />
                                        @if (isValidDefaultValue(fieldDef)) {
                                            <div class="invalid-feedback">{{ isValidDefaultValue(fieldDef)![0] }}</div>
                                        }
                                        <small class="form-text">
                                            @if (fieldDef.fieldType === "MULTISELECT") {
                                                Use | to separate multiple values
                                            } @else {
                                                Optional default value for form initialization
                                            }
                                        </small>
                                    </div>
                                </div>

                                <div class="form-row checkbox-row">
                                    <div class="form-check">
                                        <input
                                            type="checkbox"
                                            class="form-check-input"
                                            [(ngModel)]="fieldDef.hidden"
                                            id="hidden-{{ idx }}"
                                            [disabled]="isSystemField(fieldDef)"
                                        />
                                        <label class="form-check-label" for="hidden-{{ idx }}"> Hidden Field </label>
                                    </div>
                                    <div class="form-check">
                                        <input
                                            type="checkbox"
                                            class="form-check-input"
                                            [(ngModel)]="fieldDef.required"
                                            id="required-{{ idx }}"
                                            [disabled]="isSystemField(fieldDef)"
                                        />
                                        <label class="form-check-label" for="required-{{ idx }}">
                                            Required Field
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <!-- Text Field Validation -->
                            @if (fieldDef.fieldType === "TEXT") {
                                <div class="config-group">
                                    <h5>Validation Rules</h5>
                                    <div class="form-row">
                                        <div class="form-group">
                                            <label>Min Length</label>
                                            <input
                                                class="form-control"
                                                type="number"
                                                [(ngModel)]="fieldDef.minLength"
                                                min="0"
                                                [disabled]="isSystemField(fieldDef)"
                                            />
                                        </div>
                                        <div class="form-group">
                                            <label>Max Length</label>
                                            <input
                                                class="form-control"
                                                type="number"
                                                [(ngModel)]="fieldDef.maxLength"
                                                min="0"
                                                [disabled]="isSystemField(fieldDef)"
                                            />
                                        </div>
                                    </div>
                                    <div class="form-row">
                                        <div class="form-group">
                                            <label>Validation Pattern</label>
                                            <input
                                                class="form-control"
                                                [class.is-invalid]="!isValidRegex(fieldDef.regexValid)"
                                                type="text"
                                                [(ngModel)]="fieldDef.regexValid"
                                                placeholder="e.g., ^[A-Za-z0-9]+$"
                                                [disabled]="isSystemField(fieldDef)"
                                            />
                                            @if (
                                                fieldDef.regexValid &&
                                                fieldDef.regexValid.trim() &&
                                                !isValidRegex(fieldDef.regexValid)
                                            ) {
                                                <div class="invalid-feedback">Invalid regex pattern</div>
                                            }
                                            <small class="form-text">Optional regex pattern for validation</small>
                                        </div>
                                    </div>
                                </div>
                            }

                            <!-- Select Options -->
                            @if (fieldDef.fieldType === "SELECT" || fieldDef.fieldType === "MULTISELECT") {
                                <div class="config-group">
                                    <h5>Answer Options</h5>
                                    <div class="options-list">
                                        <div s25-ng-dnd-sortable [items]="fieldDef.options || []">
                                            @for (opt of fieldDef.options; track opt; let optIdx = $index) {
                                                <div class="option-item" s25-ng-dnd-sortable-item [index]="optIdx">
                                                    <s25-ng-drag-handle></s25-ng-drag-handle>
                                                    <input
                                                        class="form-control"
                                                        type="text"
                                                        [(ngModel)]="opt.option"
                                                        name="option-{{ idx }}-{{ optIdx }}"
                                                        placeholder="Option text"
                                                    />
                                                    <button
                                                        class="aw-button aw-button--danger--outline"
                                                        (click)="removeOption(fieldDef, opt)"
                                                        title="Remove Option"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            }
                                        </div>
                                        <div class="add-option">
                                            <input
                                                class="form-control"
                                                type="text"
                                                [(ngModel)]="fieldDef.newAnswer!.text"
                                                name="new-option-{{ idx }}"
                                                placeholder="Enter new option"
                                            />
                                            <button class="aw-button aw-button--outline" (click)="addOption(fieldDef)">
                                                + Add Option
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                }
            </div>

            <div class="form-actions c-margin-top--double">
                <s25-ng-button [type]="'primary'" [onClick]="saveAll">Save Form & Fields</s25-ng-button>
                @if (ingestionPoint?.deleted) {
                    <s25-ng-button [type]="'success'" [onClick]="undeleteForm" [buttonClass]="'c-margin-left--single'">
                        Restore Form
                    </s25-ng-button>
                }
                <button class="c-margin-left--single aw-button aw-button--danger--outline" (click)="cancel()">
                    Cancel
                </button>
            </div>

            <!-- Show/Hide Field Rules -->
            <h3 class="c-margin-top--double c-margin-bottom--single">Show/Hide Field Rules</h3>
            <p class="rule-description">Configure rules to dynamically show or hide fields based on user answers.</p>
            <s25-ng-rule-tree
                class="ngBlock c-margin-bottom--double"
                [category]="'ingestion'"
                [subCategory]="ingestionPointId?.toString() || ''"
                [conditionFilterMap]="showHideConditionFilterMap"
            >
            </s25-ng-rule-tree>

            <!-- Form Validation Rules -->
            <h3 class="c-margin-top--double c-margin-bottom--single">Form Validation Rules</h3>
            <p class="rule-description">
                Configure custom validation rules that check multiple fields and display form-level errors.
            </p>
            <s25-ng-rule-tree
                class="ngBlock c-margin-bottom--double"
                [category]="'ingestionValid'"
                [subCategory]="ingestionPointId?.toString() || ''"
                [conditionFilterMap]="validationConditionFilterMap"
            >
            </s25-ng-rule-tree>
        }
    `,
    styles: `
        .form-details-section {
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            padding: 20px;
        }

        .category-display {
            padding: 0.5rem;
            background-color: #e9ecef;
            border: 1px solid #ced4da;
            border-radius: 4px;
            min-height: 38px;
            display: flex;
            align-items: center;
            font-weight: 500;
            color: #495057;
        }

        .form-fields-section {
            background: #ffffff;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            padding: 24px;
        }

        .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 24px;
            padding-bottom: 16px;
            border-bottom: 2px solid #e9ecef;
        }

        .section-header h3 {
            margin: 0;
            color: #495057;
            font-weight: 600;
        }

        .field-card {
            background: #ffffff;
            border: 1px solid #e9ecef;
            border-radius: 8px;
            margin-bottom: 20px;
            overflow: hidden;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
            transition: box-shadow 0.2s ease;
        }

        .field-card:hover {
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .field-header {
            background: #f8f9fa;
            padding: 16px 20px;
            border-bottom: 1px solid #e9ecef;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .field-title {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .field-title h4 {
            margin: 0;
            color: #495057;
            font-weight: 600;
            font-size: 16px;
        }

        .field-badges {
            display: flex;
            gap: 6px;
        }

        .badge {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .badge-primary {
            background-color: #007bff;
            color: white;
        }

        .badge-secondary {
            background-color: #6c757d;
            color: white;
        }

        .badge-warning {
            background-color: #ffc107;
            color: #212529;
        }

        .action-controls {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .move-buttons {
            display: flex;
            gap: 4px;
        }

        .move-btn {
            min-width: 32px;
            padding: 4px 8px;
            font-size: 14px;
            font-weight: bold;
        }

        .field-config {
            padding: 20px;
        }

        .config-group {
            margin-bottom: 24px;
        }

        .config-group:last-child {
            margin-bottom: 0;
        }

        .config-group h5 {
            margin: 0 0 16px 0;
            color: #495057;
            font-weight: 600;
            font-size: 14px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            border-bottom: 1px solid #e9ecef;
            padding-bottom: 8px;
        }

        .form-row {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 16px;
            margin-bottom: 16px;
        }

        .form-row:last-child {
            margin-bottom: 0;
        }

        .form-group {
            display: flex;
            flex-direction: column;
        }

        .form-group label {
            font-weight: 500;
            color: #495057;
            margin-bottom: 6px;
            font-size: 13px;
        }

        .form-control {
            padding: 8px 12px;
            border: 1px solid #ced4da;
            border-radius: 4px;
            font-size: 14px;
            transition:
                border-color 0.15s ease-in-out,
                box-shadow 0.15s ease-in-out;
        }

        .form-control:focus {
            border-color: #007bff;
            outline: 0;
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }

        .form-control.is-invalid {
            border-color: #dc3545;
        }

        .form-control.is-invalid:focus {
            border-color: #dc3545;
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
        }

        .invalid-feedback {
            color: #dc3545;
            font-size: 12px;
            margin-top: 4px;
        }

        .form-text {
            color: #6c757d;
            font-size: 12px;
            margin-top: 4px;
        }

        .checkbox-row {
            display: flex;
            gap: 24px;
            margin-top: 8px;
        }

        .form-check {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .form-check-input {
            margin: 0;
            /* Override any global styles that might hide checkboxes */
            width: 1em !important;
            height: 1em !important;
            border: 1px solid #ced4da !important;
            background-color: #fff !important;
            appearance: checkbox !important;
            -webkit-appearance: checkbox !important;
            -moz-appearance: checkbox !important;
            clip: auto !important;
            clip-path: none !important;
            color: inherit !important;
            font-size: inherit !important;
            overflow: visible !important;
            padding: 0 !important;
            box-shadow: none !important;
            position: static !important;
            cursor: pointer;
        }

        .form-check-input:focus {
            border-color: #007bff !important;
            outline: 0 !important;
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
        }

        .form-check-input:checked {
            background-color: #007bff !important;
            border-color: #007bff !important;
        }

        .form-check-input:disabled {
            background-color: #e9ecef !important;
            border-color: #ced4da !important;
            cursor: not-allowed !important;
        }

        .form-check-label {
            margin: 0;
            font-size: 13px;
            color: #495057;
            cursor: pointer;
        }

        .options-list {
            border: 1px solid #e9ecef;
            border-radius: 4px;
            padding: 12px;
            background-color: #f8f9fa;
        }

        .option-item {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;
        }

        .option-item:last-child {
            margin-bottom: 0;
        }

        .add-option {
            display: flex;
            gap: 8px;
            margin-top: 12px;
            padding-top: 12px;
            border-top: 1px solid #dee2e6;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
            .form-row {
                grid-template-columns: 1fr;
            }

            .field-header {
                flex-direction: column;
                align-items: flex-start;
                gap: 12px;
            }

            .action-controls {
                align-self: flex-end;
            }

            .checkbox-row {
                flex-direction: column;
                gap: 12px;
            }

            .add-option {
                flex-direction: column;
            }
        }

        .form-actions {
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            padding: 20px;
            text-align: center;
        }

        .rule-description {
            color: #666;
            font-style: italic;
            margin-bottom: 1rem;
            font-size: 0.9rem;
        }

        .workflow-assignment-section {
            border: 1px solid #dee2e6;
            border-radius: 6px;
            padding: 16px;
            background-color: #f8f9fa;
            margin-top: 8px;
        }

        .workflow-option {
            margin-bottom: 12px;
        }

        .workflow-option:last-child {
            margin-bottom: 0;
        }

        .workflow-option-label {
            display: flex;
            align-items: center;
            cursor: pointer;
            font-weight: 500;
            color: #495057;
        }

        .workflow-option-label input[type="radio"] {
            margin-right: 8px;
        }

        .workflow-option-text {
            font-size: 14px;
        }

        .workflow-selector {
            margin-top: 8px;
            margin-left: 24px;
        }

        .workflow-selector select {
            width: 100%;
            max-width: 400px;
        }

        .date-range-section {
            border: 1px solid #dee2e6;
            border-radius: 6px;
            padding: 16px;
            background-color: #f8f9fa;
            margin-top: 8px;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class IngestionAdminFormComponent implements OnInit {
    @Input() ingestionPointId: number;
    @Output() onSave = new EventEmitter<void>();
    @Output() onCancel = new EventEmitter<void>();

    // Expose AnswerType enum to template
    AnswerType = AnswerType;

    ingestionPoint: IngestionPoint;
    fieldDefinitions: IngestionFieldDefinition[];
    showHideConditionFilterMap: Rules.ConditionFilterMap;
    validationConditionFilterMap: Rules.ConditionFilterMap;
    init = false;

    // Workflow processes
    availableWorkflows: Workflow.WorkflowProcess[] = [];
    workflowAssignmentType: "none" | "direct" | "default" = "none";

    constructor(public cd: ChangeDetectorRef) {}

    onFieldTypeChange = (fieldDef: IngestionFieldDefinition) => {
        if (!fieldDef?.fieldType) return;
        if (fieldDef.fieldType === "SECTION") {
            // Section fields don't have answer types or options
            fieldDef.answerType = undefined;
            fieldDef.options = [];
            fieldDef.required = false;
            fieldDef.minLength = undefined;
            fieldDef.maxLength = undefined;
            fieldDef.regexValid = undefined;
        } else if (fieldDef.fieldType === "FILE") {
            // FILE fields must have FILE answer type
            fieldDef.answerType = AnswerType.FILE;
            fieldDef.options = [];
            fieldDef.minLength = undefined;
            fieldDef.maxLength = undefined;
            fieldDef.regexValid = undefined;
        } else if (fieldDef.fieldType === "SELECT" || fieldDef.fieldType === "MULTISELECT") {
            // Default to STRING for select fields
            if (!fieldDef.answerType || fieldDef.answerType === AnswerType.FILE) {
                fieldDef.answerType = AnswerType.STRING;
            }
            if (!fieldDef.options) {
                fieldDef.options = [];
            }
            // no need to set minLength, maxLength, or regexValid for select fields
            fieldDef.minLength = undefined;
            fieldDef.maxLength = undefined;
            fieldDef.regexValid = undefined;
        } else if (["EVENT_TYPE", "ORGANIZATION", "FORM"].includes(fieldDef.fieldType)) {
            // EVENT_TYPE fields must have INTEGER answer type
            fieldDef.answerType = AnswerType.INTEGER;
            fieldDef.options = [];
            fieldDef.minLength = undefined;
            fieldDef.maxLength = undefined;
            fieldDef.regexValid = undefined;
        } else if (fieldDef.fieldType === "TEXT") {
            // Always set to STRING for text fields (User Input)
            fieldDef.answerType = AnswerType.STRING;
            fieldDef.options = [];
        } else if (this.isJsonField(fieldDef)) {
            // isJsonField is true for OBJECT_OCCURRENCE and REGISTRANTS
            fieldDef.answerType = AnswerType.JSON;
            fieldDef.options = [];
            fieldDef.minLength = undefined;
            fieldDef.maxLength = undefined;
            fieldDef.regexValid = undefined;
        }
        this.cd.detectChanges();
    };

    onAnswerTypeChange = (fieldDef: IngestionFieldDefinition) => {
        // Prevent changing answer type for FILE fields
        if (fieldDef.fieldType === "FILE") {
            fieldDef.answerType = AnswerType.FILE;
            return;
        }

        // Clear options when answer type changes for SELECT or MULTISELECT fields
        if (fieldDef.fieldType === "SELECT" || fieldDef.fieldType === "MULTISELECT") {
            fieldDef.options = [];
        }
        this.cd.detectChanges();
    };

    onSubCategoryChange = (subCategory: string) => {
        this.ingestionPoint.subCat = subCategory;
        this.cd.detectChanges();
    };

    addOption = (fieldDef: IngestionFieldDefinition) => {
        let option = fieldDef.newAnswer?.text;
        if (option) {
            if (fieldDef.newAnswer) fieldDef.newAnswer.text = "";
            if (!fieldDef.options) fieldDef.options = [];
            fieldDef.options.push({
                option: option,
                fieldDefinitionId: fieldDef.fieldDefinitionId,
                sortOrder: fieldDef.options.length + 1,
            });
            let idx = this.fieldDefinitions.indexOf(fieldDef);
            this.fieldDefinitions.splice(idx, 1);
            this.cd.detectChanges();
            this.fieldDefinitions.splice(idx, 0, fieldDef);
        }
        this.cd.detectChanges();
    };

    removeOption = (fieldDef: IngestionFieldDefinition, option: { option?: string }) => {
        if (!fieldDef.options) return;
        for (let i = fieldDef.options.length - 1; i >= 0; i--) {
            if (fieldDef.options[i].option === option.option) {
                fieldDef.options.splice(i, 1);
            }
        }
        this.cd.detectChanges();
    };

    addFieldDefinition = () => {
        const newField: IngestionFieldDefinition = {
            fieldLabel: "New Field! Remember to Save When Finished.",
            fieldType: "SELECT",
            answerType: AnswerType.STRING,
            options: [],
            newAnswer: { text: "" },
            required: false,
            hidden: false,
        };
        // Auto-generate reporting code for new field
        newField.reportingFieldCode = this.generateReportingCode(newField.fieldLabel!);
        // Initialize prevFieldLabel for auto-generation logic
        newField.prevFieldLabel = newField.fieldLabel;
        this.fieldDefinitions.push(newField);
        this.cd.detectChanges();
    };

    removeFieldDefinition = (fieldDef: IngestionFieldDefinition) => {
        this.fieldDefinitions.splice(this.fieldDefinitions.indexOf(fieldDef), 1);
        this.cd.detectChanges();
        return;
    };

    move = (fieldDef: IngestionFieldDefinition, dir: number) => {
        let idx = this.fieldDefinitions.indexOf(fieldDef);

        if (dir === 1 && idx === this.fieldDefinitions.length - 1) {
            return;
        }

        if (dir === -1 && idx === 0) {
            return;
        }

        let temp = this.fieldDefinitions[idx + dir];
        this.fieldDefinitions[idx + dir] = fieldDef;
        this.fieldDefinitions[idx] = temp;

        for (let i = 0; i < this.fieldDefinitions.length; i++) {
            this.fieldDefinitions[i].sortOrder = i;
        }

        this.fieldDefinitions.sort(S25Util.shallowSort("sortOrder", true));
        this.cd.detectChanges();
    };

    isValidRegex = (pattern: string | undefined): boolean => {
        if (!pattern || !pattern.trim()) {
            return true; // Empty patterns are considered valid (optional)
        }

        try {
            new RegExp(pattern);
            return true;
        } catch (error) {
            return false;
        }
    };

    validateRegexPatterns = (): string[] => {
        const errors: string[] = [];

        this.fieldDefinitions.forEach((fieldDef, index) => {
            if (fieldDef.fieldType === "TEXT" && fieldDef.regexValid && fieldDef.regexValid.trim()) {
                if (!this.isValidRegex(fieldDef.regexValid)) {
                    const fieldLabel = fieldDef.fieldLabel || `Field ${index + 1}`;
                    errors.push(`${fieldLabel}: Invalid regex pattern "${fieldDef.regexValid}"`);
                }
            }
        });

        return errors;
    };

    isValidReportingCode = (code: string | undefined): boolean => {
        if (!code || !code.trim()) {
            return true; // Empty codes are considered valid (optional)
        }
        // Only alphanumeric characters, max 32 characters
        return /^[A-Za-z0-9]{1,32}$/.test(code);
    };

    generateReportingCode = (fieldLabel: string): string => {
        if (!fieldLabel || !fieldLabel.trim()) {
            return "";
        }

        // Remove all non-alphanumeric characters and convert to camelCase
        let code = fieldLabel
            .replace(/[^A-Za-z0-9\s]/g, "") // Remove special characters but keep spaces
            .split(/\s+/) // Split by whitespace
            .filter((word) => word.length > 0) // Remove empty strings
            .map((word, index) => {
                if (index === 0) {
                    return word.toLowerCase(); // First word lowercase
                } else {
                    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(); // Subsequent words capitalized
                }
            })
            .join(""); // Join without spaces

        // Truncate to 32 characters max
        return code.substring(0, 32);
    };

    onFieldLabelChange = (fieldDef: IngestionFieldDefinition) => {
        // Auto-generate reporting code if it's empty or was previously auto-generated
        if (
            !fieldDef.reportingFieldCode ||
            fieldDef.reportingFieldCode === this.generateReportingCode(fieldDef.prevFieldLabel || "")
        ) {
            fieldDef.reportingFieldCode = this.generateReportingCode(fieldDef.fieldLabel || "");
        }
        // Update the previous field label for next comparison
        fieldDef.prevFieldLabel = fieldDef.fieldLabel;
        this.cd.detectChanges();
    };

    onReportingCodeChange = (fieldDef: IngestionFieldDefinition) => {
        // Ensure the code is valid
        if (fieldDef.reportingFieldCode && !this.isValidReportingCode(fieldDef.reportingFieldCode)) {
            // Don't prevent typing, but the validation will show an error
        }
        this.cd.detectChanges();
    };

    validateReportingCodes = (): string[] => {
        const errors: string[] = [];

        this.fieldDefinitions.forEach((fieldDef, index) => {
            if (fieldDef.reportingFieldCode && fieldDef.reportingFieldCode.trim()) {
                if (!this.isValidReportingCode(fieldDef.reportingFieldCode)) {
                    const fieldLabel = fieldDef.fieldLabel || `Field ${index + 1}`;
                    errors.push(
                        `${fieldLabel}: Invalid reporting code "${fieldDef.reportingFieldCode}" - only alphanumeric characters allowed, max 32 characters`,
                    );
                }
            }
        });

        return errors;
    };

    refresh = () => {
        S25Util.all({
            ingestionPoint: DataIngestionService.getIngestionPoint(this.ingestionPointId),
            fieldDefinitions: DataIngestionService.getFieldDefinitions(this.ingestionPointId),
        }).then((resp) => {
            this.ingestionPoint = resp.ingestionPoint;
            this.determineWorkflowAssignmentType();

            // Initialize admin-specific fields for field definitions
            for (let fieldDef of resp.fieldDefinitions) {
                fieldDef.newAnswer = { text: "" };
                if (!fieldDef.options) {
                    fieldDef.options = [];
                }
            }
            this.fieldDefinitions = resp.fieldDefinitions;
            this.init = true;
            this.cd.detectChanges();
        });
    };

    saveAll = async () => {
        // Validate form name
        if (!this.ingestionPoint.ingestionPointName?.trim()) {
            S25Util.showError("Form name is required");
            return;
        }

        if (!this.ingestionPoint.category) {
            S25Util.showError("Category is required");
            return;
        }

        // Validate all regex patterns before saving
        const validationErrors = this.validateRegexPatterns();
        if (validationErrors.length > 0) {
            S25Util.showError("Invalid regex patterns found:\n" + validationErrors.join("\n"));
            return;
        }

        // Validate all reporting codes before saving
        const reportingCodeErrors = this.validateReportingCodes();
        if (reportingCodeErrors.length > 0) {
            S25Util.showError("Invalid reporting codes found:\n" + reportingCodeErrors.join("\n"));
            return;
        }

        // Validate all default values before saving
        const defaultValueErrors = this.validateDefaultValues();
        if (defaultValueErrors.length > 0) {
            S25Util.showError("Invalid default values found:\n" + defaultValueErrors.join("\n"));
            return;
        }

        try {
            // Save form details first
            await DataIngestionService.updateIngestionPoint(this.ingestionPointId, this.ingestionPoint);

            // Then save field definitions
            await DataIngestionService.replaceFieldDefinitions(this.ingestionPointId, this.fieldDefinitions);

            this.onSave.emit();
        } catch (err) {
            S25Util.showError(err);
        }
    };

    cancel = () => {
        this.onCancel.emit();
    };

    undeleteForm = async () => {
        try {
            await DataIngestionService.undeleteIngestionPoint(this.ingestionPointId);
            this.onSave.emit();
        } catch (err) {
            S25Util.showError(err);
        }
    };

    ngOnInit() {
        this.showHideConditionFilterMap = {
            ingestionFieldsAll: "" + this.ingestionPointId,
        };
        this.validationConditionFilterMap = {
            ingestionFieldsAll: "" + this.ingestionPointId,
        };
        this.loadAvailableWorkflows();
        this.refresh();
    }

    async loadAvailableWorkflows() {
        try {
            this.availableWorkflows = await WorkflowService.getWorkflowProcesses(true);
            this.cd.detectChanges();
        } catch (error) {
            console.error("Error loading workflow processes:", error);
        }
    }

    onWorkflowTypeChange() {
        // Clear the other workflow field when switching types
        if (this.workflowAssignmentType === "direct") {
            this.ingestionPoint.defaultWorkflowId = undefined;
        } else if (this.workflowAssignmentType === "default") {
            this.ingestionPoint.workflowId = undefined;
        } else if (this.workflowAssignmentType === "none") {
            this.ingestionPoint.workflowId = undefined;
            this.ingestionPoint.defaultWorkflowId = undefined;
        }
        this.cd.detectChanges();
    }

    determineWorkflowAssignmentType() {
        if (this.ingestionPoint.workflowId) {
            this.workflowAssignmentType = "direct";
        } else if (this.ingestionPoint.defaultWorkflowId) {
            this.workflowAssignmentType = "default";
        } else {
            this.workflowAssignmentType = "none";
        }
    }

    getDefaultValuePlaceholder(fieldDef: IngestionFieldDefinition): string {
        switch (fieldDef.answerType) {
            case "JSON":
                return "";
            case "INTEGER":
                return "e.g., 123";
            case "FLOAT":
                return "e.g., 123.45";
            case "BOOLEAN":
                return "true or false";
            case "DATETIME":
                return "e.g., 2024-01-15T10:30:00";
            case "STRING":
            case "LARGE_STRING":
                return "e.g., Default text value";
            case "FILE":
                return "File path or URL";
            default:
                if (fieldDef.fieldType === "MULTISELECT") {
                    return "e.g., Option 1|Option 2 (use | to separate multiple values)";
                }
                return "Enter default value";
        }
    }

    isValidDefaultValue(fieldDef: IngestionFieldDefinition): string[] | null {
        if (!fieldDef.defaultValue || fieldDef.defaultValue.trim() === "") {
            return null; // Empty is valid (optional field)
        }

        const value = fieldDef.defaultValue.trim();
        const errors: string[] = [];

        switch (fieldDef.answerType) {
            case "JSON":
                break;

            case "INTEGER":
                const intValue = parseInt(value, 10);
                if (isNaN(intValue) || intValue.toString() !== value) {
                    errors.push("Must be a valid integer (e.g., 123)");
                }
                break;

            case "FLOAT":
                const floatValue = parseFloat(value);
                if (isNaN(floatValue) || !isFinite(floatValue)) {
                    errors.push("Must be a valid number (e.g., 123.45)");
                }
                break;

            case "BOOLEAN":
                const lowerValue = value.toLowerCase();
                if (!["true", "false", "1", "0"].includes(lowerValue)) {
                    errors.push("Must be 'true', 'false', '1', or '0'");
                }
                break;

            case "DATETIME":
                const date = new Date(value);
                if (isNaN(date.getTime())) {
                    errors.push("Must be a valid date/time (e.g., 2024-01-15T10:30:00)");
                }
                break;

            case "STRING":
            case "LARGE_STRING":
            case "FILE":
                if (fieldDef.options && fieldDef.options.length > 0) {
                    if (fieldDef.fieldType === "MULTISELECT") {
                        // For multiselect, split by | and validate each value
                        const values = value
                            .split("|")
                            .map((v) => v.trim())
                            .filter((v) => v);
                        const invalidValues = values.filter((v) => !fieldDef.options!.some((opt) => opt.option === v));
                        if (invalidValues.length > 0) {
                            errors.push(
                                `Invalid values: ${invalidValues.join(", ")}. Must match available options: ${fieldDef.options.map((opt) => opt.option).join(", ")}`,
                            );
                        }
                    } else {
                        const hasMatchingOption = fieldDef.options.some((opt) => opt.option === value);
                        if (!hasMatchingOption) {
                            errors.push(
                                `Must match one of the available options: ${fieldDef.options.map((opt) => opt.option).join(", ")}`,
                            );
                        }
                    }
                }
                break;
        }

        return errors.length > 0 ? errors : null;
    }

    /**
     * Checks if a field definition is a system-defined field (ID < 0)
     * System-defined fields cannot be edited or removed by users
     */
    isSystemField(fieldDef: IngestionFieldDefinition): boolean {
        return fieldDef.fieldDefinitionId != null && fieldDef.fieldDefinitionId < 0;
    }

    validateDefaultValues(): string[] {
        const errors: string[] = [];

        this.fieldDefinitions.forEach((fieldDef, index) => {
            const fieldErrors = this.isValidDefaultValue(fieldDef);
            if (fieldErrors) {
                fieldErrors.forEach((error) => {
                    errors.push(`Field "${fieldDef.fieldLabel}" (${index + 1}): ${error}`);
                });
            }
        });

        return errors;
    }

    isJsonField(fieldDef: IngestionFieldDefinition): boolean {
        return DataIngestionService.isJsonField(fieldDef);
    }
}
